import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  body: string | null;
  read_at: string | null;
  data: Record<string, unknown>;
  created_at: string;
}

const TYPE_ICON_MAP: Record<string, { icon: string; color: string; bg: string }> = {
  welcome:             { icon: 'ri-hand-heart-line',     color: 'text-emerald-500', bg: 'bg-emerald-50' },
  payment_approved:    { icon: 'ri-checkbox-circle-fill',  color: 'text-emerald-500', bg: 'bg-emerald-50' },
  payment_rejected:    { icon: 'ri-close-circle-fill',     color: 'text-red-500',     bg: 'bg-red-50'     },
  payment_pending:     { icon: 'ri-time-fill',             color: 'text-yellow-500',  bg: 'bg-yellow-50'  },
  upgrade_completed:   { icon: 'ri-vip-crown-fill',        color: 'text-amber-500',   bg: 'bg-amber-50'   },
  plan_limit_reached:  { icon: 'ri-error-warning-fill',    color: 'text-orange-500',  bg: 'bg-orange-50'  },
  goal_completed:      { icon: 'ri-trophy-fill',           color: 'text-emerald-500', bg: 'bg-emerald-50' },
  account_expired:     { icon: 'ri-calendar-close-fill',   color: 'text-red-500',     bg: 'bg-red-50'     },
  card_expiring:       { icon: 'ri-bank-card-fill',        color: 'text-orange-500',  bg: 'bg-orange-50'  },
  card_due_reminder:   { icon: 'ri-bank-card-2-fill',      color: 'text-red-500',     bg: 'bg-red-50'     },
  broadcast:           { icon: 'ri-megaphone-fill',        color: 'text-violet-500',  bg: 'bg-violet-50'  },
  calendar_pending:    { icon: 'ri-calendar-todo-fill',    color: 'text-amber-500',   bg: 'bg-amber-50'   },
  calendar_overdue:    { icon: 'ri-calendar-close-fill',   color: 'text-red-500',     bg: 'bg-red-50'     },
  info:                { icon: 'ri-information-line',      color: 'text-sky-500',     bg: 'bg-sky-50'     },
  warning:             { icon: 'ri-error-warning-line',    color: 'text-amber-500',   bg: 'bg-amber-50'   },
  success:             { icon: 'ri-check-line',            color: 'text-emerald-500', bg: 'bg-emerald-50' },
};

export const getNotificationMeta = (type: string) =>
  TYPE_ICON_MAP[type] ?? { icon: 'ri-notification-3-line', color: 'text-gray-500', bg: 'bg-gray-50' };

// Types that have a clickable CTA navigation
export const NAVIGABLE_TYPES = [
  'payment_rejected', 'payment_pending', 'upgrade_completed',
  'plan_limit_reached', 'goal_completed', 'calendar_pending', 'calendar_overdue',
];

export const useNotifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [toastNotification, setToastNotification] = useState<Notification | null>(null);

  // ── Security: clear stale notifications immediately when user changes or logs out ──
  const prevUserIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const currentUserId = user?.id;
    if (prevUserIdRef.current !== currentUserId) {
      if (prevUserIdRef.current !== undefined || currentUserId === undefined) {
        setNotifications([]);
        setToastNotification(null);
      }
      prevUserIdRef.current = currentUserId;
    }
  }, [user?.id]);

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(50);
    const safeData = ((data as Notification[]) ?? []).filter(
      (n) => n.user_id === user.id
    );
    setNotifications(safeData);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchNotifications();

    if (!user) return;

    const channel = supabase
      .channel(`user-notifications-${user.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => {
          const newNotif = payload.new as Notification;
          if (newNotif.user_id === user.id) {
            setNotifications((prev) => [newNotif, ...prev]);
            // Trigger toast for new real-time notification
            setToastNotification(newNotif);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchNotifications]);

  const dismissToast = useCallback(() => {
    setToastNotification(null);
  }, []);

  const markAllRead = useCallback(async () => {
    if (!user) return;
    const unreadIds = notifications.filter((n) => !n.read_at).map((n) => n.id);
    if (unreadIds.length === 0) return;
    const now = new Date().toISOString();
    await supabase
      .from('notifications')
      .update({ read_at: now })
      .in('id', unreadIds)
      .eq('user_id', user.id);
    setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at ?? now })));
  }, [user, notifications]);

  const markRead = useCallback(async (id: string) => {
    if (!user) return;
    const now = new Date().toISOString();
    await supabase
      .from('notifications')
      .update({ read_at: now })
      .eq('id', id)
      .eq('user_id', user.id);

    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: now } : n)));

    const notif = notifications.find((n) => n.id === id);
    if (notif?.type === 'broadcast' && notif.data?.broadcast_id && !notif.read_at) {
      const broadcastId = notif.data.broadcast_id as string;
      await supabase
        .from('broadcast_recipients')
        .update({ read_at: now })
        .eq('broadcast_id', broadcastId)
        .eq('user_id', user.id);
      await supabase.rpc('increment_broadcast_read_count', { broadcast_id_param: broadcastId });
    }
  }, [user, notifications]);

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  return {
    notifications,
    loading,
    unreadCount,
    markAllRead,
    markRead,
    refetch: fetchNotifications,
    toastNotification,
    dismissToast,
  };
};
